import { connect } from "react-redux";
import React from "react";

import { AbsoluteModelPath, AbsoluteResolvedModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { UnitId } from "../UnitId/UnitId";
import { IUnitRegistry } from "../../Navigation/UnitRegistry/IUnitRegistry";
import { CandyDispatch } from "../../../../Engine/Core/Store/CandyReduxTypes";
import { unitHighlightAction, updateEmptyUnits } from "../../../../Engine/ActionCreators/NavigationActionCreators";
import { withUnitRegistry } from "../../Navigation/UnitRegistry/UnitRegistryContext";
import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { withResolvePaths } from "../../../withResolvePaths";
import {
    clearAttachmentAction,
    removeAttachmentAction,
} from "../../../../Engine/ActionCreators/AttachmentActionCreators";
import { EvaluableProp, withEvaluators } from "../../../withEvaluators";
import { withViewModelSelect } from "../../../withViewModelSelect";
import { ViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { UnitIdSet } from "../UnitId/UnitIdImmutableSet";
import { UnitIdResolver, withResolveUnitId } from "../../../withResolveUnitId";
import { ExtraPageAction, PropsPageActions } from "../Types";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { ClearPageMethod } from "../../../../Engine/ActionCreators/UnitActionCreators";
import { FormActionHandler } from "../../../Internals/FormActionHandlers/FormActionHandler";
import { getRemoveLabelForChangeSet } from "../../UnitItem/UnitItemHelpers";

import { AttachmentFormUnit } from "./AttachmentFormUnit";

export interface AttachmentUnitContainerProps extends FormActionHandlerExecutionInjectableProps {
    gfv: string;
    unitId: UnitId;
    resolveUnitId: UnitIdResolver;
    unitRegistry: IUnitRegistry;
    bindingPath: AbsoluteResolvedModelPath;
    updateEmptyUnits: (unitId: UnitId, empty: boolean) => void;
    caption: string;
    description: string;
    onRemoveAttachment: (bindingPath: AbsoluteResolvedModelPath) => Promise<void>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    optional?: boolean;
    multiple?: boolean;
    visible?: EvaluableProp<boolean>;
    evaluatorsContextPath: AbsoluteModelPath;
    hasDefaultContent?: boolean;
    onSaveAttachmentFuf: (instancePath: string) => Promise<void>;
    onClearAttachment: (method: ClearPageMethod, instancePath: string) => Promise<void>;
    onPrintAttachment: (instancePath: string) => Promise<void>;
    viewModelSelect: ViewModelSelect;
    pageActions: PropsPageActions;
    extraPageActions?: ExtraPageAction[];
    onMount?: FormActionHandler;
    onUnmount?: FormActionHandler;
    attachmentUnitItemInfoPaths?: AbsoluteResolvedModelPath | AbsoluteResolvedModelPath[];
    emptyUnitTitle?: string;
    emptyUnitDescription?: React.ReactNode;
    children: React.ReactChildren | React.ReactNode;
}

const mapStateToProps = (state: FullFormState, ownProps: AttachmentUnitContainerProps) => {
    const resolvedUnitId = ownProps.resolveUnitId(ownProps.unitId);
    const { emptyUnits } = state;
    const filled = resolvedUnitId == undefined || !UnitIdSet.hasItem(emptyUnits, resolvedUnitId);
    const hidden = state.settings.isReadOnly && (state.settings.isFormInFormNds || !filled);

    return {
        visible: hidden ? false : ownProps.processEvaluableProp(state, ownProps.visible),
        isReadOnly: state.settings.isReadOnly,
        pageActions: {
            ...ownProps.pageActions,
            hideButton: ownProps.pageActions?.hideButton ?? !state.settings.isFormInFormNds,
        },
    };
};
const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: AttachmentUnitContainerProps) => ({
    updateEmptyUnits: (unitId: UnitId, empty: boolean) => {
        dispatch(updateEmptyUnits(unitId, empty));
    },
    onRemoveAttachment: async (bindingPath: AbsoluteResolvedModelPath) => {
        await dispatch(
            removeAttachmentAction(bindingPath, undefined, {
                operationName: getRemoveLabelForChangeSet(ownProps.caption),
            })
        );
    },
    onClearAttachment: (attachmentId: string) => dispatch(clearAttachmentAction(attachmentId)),
    onHighlightUnit: (unitId: UnitId) => {
        dispatch(unitHighlightAction(unitId));
    },
    onMount: async () => {
        if (ownProps.onMount != undefined) {
            await ownProps.executeFormActionHandler(ownProps.onMount);
        }
    },
    onUnmount: async () => {
        if (ownProps.onUnmount != undefined) {
            await ownProps.executeFormActionHandler(ownProps.onUnmount);
        }
    },
});

export const AttachmentFormUnitContainer = withResolvePaths(
    withResolveUnitId(
        withUnitRegistry(
            withFormActionHandlerExecution(
                withViewModelSelect(withEvaluators(connect(mapStateToProps, mapDispatchToProps)(AttachmentFormUnit)))
            )
        )
    )
);
